import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconSkipForward } from '@sorare/core/src/atoms/icons/system/iconSkipForward.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionAnimationCompetition } from './CollectionAnimationCompetition/index.tsx';
import type { CollectionAnimation_anyCardInterface } from './__generated__/index.graphql.ts';
import { collectionAnimationConfig } from './animationConfig.ts';

const Root = styled(Vertical)`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  height: var(--100vh);
  @media ${tabletAndAbove} {
    max-height: 85vh;
    height: 100%;
  }
  justify-content: stretch;
`;

const SkipButton = styled(IconButton)`
  position: absolute;
  bottom: var(--double-unit);
  right: var(--triple-unit);
  z-index: 10;
`;

const CompetitionsWrapper = styled(Horizontal)`
  transition: transform ${collectionAnimationConfig.competitionSwitchMs}ms
    var(--ease-out-slide);
  flex: 1;
  height: 100%;
`;

type Props = {
  shouldAnimate: boolean;
  newCards: CollectionAnimation_anyCardInterface[];
  onAnimationEnd: () => void;
  cardSetSlug: string;
};

export const CollectionAnimation = ({
  shouldAnimate,
  newCards,
  onAnimationEnd: onAnimationEndProp,
  cardSetSlug,
}: Props) => {
  const track = useEvents();
  const [currentCollectionIndex, setCurrentCollectionIndex] =
    useState<number>();

  if (shouldAnimate && currentCollectionIndex === undefined) {
    setCurrentCollectionIndex(0);
  }

  const competitions = uniqueBy(
    competition => competition.slug,
    newCards
      .map(card => card.anyTeam)
      .filter(isTypeFn('Club'))
      .map(club => club.domesticLeague)
      .filter(Boolean)
  );

  const newCardSkins = [
    ...new Set(newCards.map(card => card.skinEdition?.skin)),
  ].filter(Boolean);

  return (
    <Root>
      <CompetitionsWrapper
        style={{
          transform: `translateX(calc(-100% * ${currentCollectionIndex})`,
        }}
        gap={0}
      >
        {competitions.map((competition, index) => {
          const newCardsForCompetition = newCards.filter(card => {
            const { anyTeam } = card;
            return (
              isType(anyTeam, 'Club') &&
              anyTeam?.domesticLeague?.slug === competition.slug
            );
          });
          return (
            <CollectionAnimationCompetition
              key={competition.slug}
              newCards={newCardsForCompetition}
              competition={competition}
              shouldAnimate={index === currentCollectionIndex}
              onAnimationEnd={() => {
                if (typeof currentCollectionIndex !== 'number') {
                  return;
                }
                if (currentCollectionIndex === competitions.length - 1) {
                  onAnimationEndProp();
                  return;
                }
                setCurrentCollectionIndex(currentCollectionIndex + 1);
              }}
              cardSetSlug={cardSetSlug}
              newCardSkins={newCardSkins}
            />
          );
        })}
      </CompetitionsWrapper>
      <SkipButton
        size="medium"
        onClick={() => {
          track('Click Skip Collection Animation', {});
          onAnimationEndProp();
        }}
        color="secondary"
        icon={iconSkipForward}
      />
    </Root>
  );
};

CollectionAnimation.fragments = {
  anyCardInterface: gql`
    fragment CollectionAnimation_anyCardInterface on AnyCardInterface {
      slug
      anyTeam {
        slug
        ... on Club {
          slug
          domesticLeague {
            slug
            ...CollectionAnimationCompetition_competition
          }
        }
      }
      skinEdition {
        skin
      }
      ...CollectionAnimationCompetition_anyCardInterface
    }
    ${CollectionAnimationCompetition.fragments.anyCardInterface}
    ${CollectionAnimationCompetition.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimation_anyCardInterface>,
};
